import type { DeepPartial } from '@luna/utils-ts';
import { BUNDLE_ORDER_TYPE, FLAG, ORDER_TYPE } from '@shared/payments-types';
import { AuthorizationType } from '@shared/core-types';

import type { PaymentOrders } from './PaymentOrders';

export const AT: DeepPartial<PaymentOrders> = {
    shouldShowUserIdInAuthorizations: false,
    collectiveSigningFlag: FLAG.COLLECTIVE,
    orderBundler: {
        config: {
            [AuthorizationType.CARDTAN]: [
                {
                    combination: [BUNDLE_ORDER_TYPE.OWN_FX],
                    maxSize: 1,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    authorizationType: AuthorizationType.CARDTAN,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
                {
                    combination: [
                        BUNDLE_ORDER_TYPE.DOMESTIC,
                        BUNDLE_ORDER_TYPE.SEPA,
                        BUNDLE_ORDER_TYPE.INTERNATIONAL,
                        BUNDLE_ORDER_TYPE.OWN_TRANSFER,
                    ],
                    maxSize: 25,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    authorizationType: AuthorizationType.CARDTAN,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
                {
                    combination: [BUNDLE_ORDER_TYPE.DIRECT_DEBIT],
                    maxSize: 15,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    authorizationType: AuthorizationType.CARDTAN,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
            ],
            [AuthorizationType.SID]: [
                {
                    combination: [BUNDLE_ORDER_TYPE.OWN_FX],
                    maxSize: 1,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: false,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    authorizationType: AuthorizationType.SID,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
                {
                    combination: [
                        BUNDLE_ORDER_TYPE.DOMESTIC,
                        BUNDLE_ORDER_TYPE.SEPA,
                        BUNDLE_ORDER_TYPE.INTERNATIONAL,
                        BUNDLE_ORDER_TYPE.OWN_TRANSFER,
                    ],
                    maxSize: 25,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    authorizationType: AuthorizationType.SID,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
                {
                    combination: [BUNDLE_ORDER_TYPE.DIRECT_DEBIT],
                    maxSize: 15,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    authorizationType: AuthorizationType.SID,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
            ],
            [AuthorizationType.GEORGE_TOKEN]: [
                {
                    combination: [
                        BUNDLE_ORDER_TYPE.DOMESTIC,
                        BUNDLE_ORDER_TYPE.SEPA,
                        BUNDLE_ORDER_TYPE.INTERNATIONAL,
                        BUNDLE_ORDER_TYPE.OWN_TRANSFER,
                    ],
                    maxSize: 10,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    authorizationType: AuthorizationType.GEORGE_TOKEN,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
                {
                    combination: [BUNDLE_ORDER_TYPE.DIRECT_DEBIT],
                    maxSize: 10,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    authorizationType: AuthorizationType.GEORGE_TOKEN,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
            ],
            DEFAULT: [
                {
                    combination: [BUNDLE_ORDER_TYPE.OWN_FX],
                    maxSize: 1,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: false,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
                {
                    combination: [BUNDLE_ORDER_TYPE.DOMESTIC],
                    maxSize: 5,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
                {
                    combination: [
                        BUNDLE_ORDER_TYPE.SEPA,
                        BUNDLE_ORDER_TYPE.INTERNATIONAL,
                        BUNDLE_ORDER_TYPE.OWN_TRANSFER,
                    ],
                    maxSize: 3,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
                {
                    combination: [BUNDLE_ORDER_TYPE.DIRECT_DEBIT],
                    maxSize: 50,
                    mixCurrenciesAllowed: false,
                    mixSenderAccountsAllowed: true,
                    mixDifferentCurrencySenderAccounts: false,
                    mixOrderAndSenderAccountCurrencyAllowed: true,
                    forbiddenOrderTypes: [ORDER_TYPE.INSTANT_E_PAYMENT_OUT],
                },
            ],
        },
    },
    failedOrderStates: [
        'KAG',
        'SNM',
        'NGA',
        'ADB',
        'AGB',
        'OBG',
        'UNG',
        'BNZ',
        'ENE',
        'BLA',
        'FUS',
        'ABG',
        'UNK',
        'DDB',
        'DDD',
        'ABG',
        'RED',
        'XPR',
        'BAM',
        'BAN',
        'BPA',
        'BPR',
        'CBA',
        'EEI',
        'FRD',
        'IPA',
        'IPC',
        'IPE',
        'ISA',
        'RBC',
        'REC',
    ],
    waitingOrderStates: [
        'AFU',
        'AFP',
        'CHK',
        'COS',
        'INB',
        'NGD',
        'OFL',
        'PSI',
        'RPS',
        'SUS',
        'WEA',
        'WEP',
        'WEU',
    ],
    verificationOfPayee: {
        showSigningDescriptionForMatch: false,
    },
    dataCarrier: {
        useStateDetailForBadge: true,
    },
    requestDoubleSigningInCaseOfPossibleFraud: true,
};
